import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import ButtonGroup from "@mui/joy/ButtonGroup";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Step from "@mui/joy/Step";
import StepIndicator from "@mui/joy/StepIndicator";
import Stepper from "@mui/joy/Stepper";
import Typography from "@mui/joy/Typography";
import React from "react";
import { StudyQuartzClientContext } from "../../../shared/services/api/contexts";
import { DrawerWrapper } from "../../../shared/components/layout/DrawerWrapper";
import {
  Participant,
  ParticipantByState,
  ParticipantEventToState,
  ParticipantId,
  ParticipantProgress,
  PutParticipantEventRequest,
} from "../../../models/study";
import { formatAndCapitalizeString } from "../../../shared/utils/text";
import { unixToDayjs } from "../../../shared/utils/time";
import NewParticipantActionModal from "./NewParticipantActionModal";
import { getParticipantCurrentState } from "../pages/StudyPage";

interface StudyParticipantDrawerProps {
  readonly selectedIds: ParticipantId[];
  readonly selectedParticipants: Participant[];
  readonly participantByState: ParticipantByState[];
  readonly closeModal: () => void;
  readonly refresh: () => void;
}

export default function StudyParticipantDrawer({
  selectedIds,
  selectedParticipants,
  participantByState,
  closeModal,
  refresh,
}: StudyParticipantDrawerProps) {
  // Contexts
  const studyQuartzClient = React.useContext(StudyQuartzClientContext);

  // States
  const [participantIdentifier, setParticipantIdentifier] = React.useState<string>("");
  const [commonNextEvents, setCommonNextEvents] = React.useState<ParticipantEventToState[]>([]);
  const [selectedNextEvent, setSelectedNextEvent] = React.useState<ParticipantEventToState | null>(
    null
  );
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

  // Handlers
  const handleAddParticipantEvent = async (
    putParticipantEventRequest: PutParticipantEventRequest
  ) => {
    await studyQuartzClient.putParticipantEvent(putParticipantEventRequest);
    refresh();
  };

  const onEditParticipant = async (participant: Participant) => {
    const updatedParticipant: Participant = {
      ...participant,
      identifier: [
        { value: participantIdentifier ? participantIdentifier.trim() : "", assigner: "GIMLI" },
      ],
    };

    setIsUpdating(true);
    try {
      await studyQuartzClient.updateParticipant(updatedParticipant);
      refresh();
    } catch (error) {
      console.error("Error updating participant:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper functions
  const getSelectedParticipantStates = React.useCallback(() => {
    return selectedParticipants.map(
      (participant) => getParticipantCurrentState(participant)?.state
    );
  }, [selectedParticipants]);

  const getFilteredParticipantStates = React.useCallback(
    (selectedStates: (string | undefined)[]) => {
      return participantByState.filter((pbs) => {
        return selectedStates.includes(pbs.state);
      });
    },
    [participantByState]
  );

  const calculateCommonNextEvents = React.useCallback((filteredStates: ParticipantByState[]) => {
    if (filteredStates.length === 0) {
      return null;
    }

    return filteredStates
      .map((pbs) => pbs.next_events)
      .reduce((acc, val) => {
        if (!acc || !val) return acc;
        const eventNames = val.map((v: ParticipantEventToState) => v.event);
        return acc.filter((event) => eventNames.includes(event.event));
      }, filteredStates[0].next_events);
  }, []);

  const updateParticipantIdentifier = React.useCallback(() => {
    const shouldUpdateIdentifier =
      selectedParticipants.length === 1 && selectedParticipants[0].identifier.length === 1;

    if (shouldUpdateIdentifier) {
      setParticipantIdentifier(selectedParticipants[0].identifier[0].value);
    }
  }, [selectedParticipants]);

  // Effects
  React.useEffect(() => {
    if (selectedParticipants.length === 0) {
      return;
    }

    const selectedStates = getSelectedParticipantStates();
    const filteredStates = getFilteredParticipantStates(selectedStates);
    const commonEvents = calculateCommonNextEvents(filteredStates);

    setCommonNextEvents(commonEvents || []);
    updateParticipantIdentifier();
  }, [
    selectedParticipants,
    participantByState,
    getSelectedParticipantStates,
    getFilteredParticipantStates,
    calculateCommonNextEvents,
    updateParticipantIdentifier,
  ]);

  return (
    <DrawerWrapper>
      {selectedNextEvent && (
        <NewParticipantActionModal
          event={selectedNextEvent}
          participantIds={selectedIds}
          handleAddParticipantAction={handleAddParticipantEvent}
          onClose={() => setSelectedNextEvent(null)}
        />
      )}
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <Typography level="title-md" sx={{ flex: 1 }}>
          {selectedParticipants.length} participant{selectedParticipants.length > 1 && "s"} selected
        </Typography>
        <IconButton component="span" variant="plain" color="neutral" size="sm">
          <CloseRoundedIcon onClick={closeModal} />
        </IconButton>
      </Box>
      <Divider />
      <Stack
        sx={{ flex: 1, background: "white" }}
        direction="column"
        justifyContent="space-between"
      >
        {selectedParticipants.length === 1 ? (
          <Stack direction="column" gap={2} sx={{ p: 2 }}>
            <FormControl>
              <FormLabel>Study unique identifier</FormLabel>
              <Input
                value={participantIdentifier}
                onChange={(event) => setParticipantIdentifier(event.target.value)}
                endDecorator={
                  isUpdating ? (
                    <CircularProgress />
                  ) : (
                    <Button onClick={() => onEditParticipant(selectedParticipants[0])}>Save</Button>
                  )
                }
              />
            </FormControl>
            <Divider />
            <Typography sx={{ mb: 2 }} level="title-lg">
              Participant progress
            </Typography>
            <Stepper orientation="vertical">
              {selectedParticipants[0].progress.map(
                (progress: ParticipantProgress, index: number) => (
                  <Step
                    key={`${progress.state}-${progress.start_date || index}`}
                    indicator={
                      <StepIndicator
                        variant="soft"
                        color={
                          index + 1 === selectedParticipants[0].progress.length
                            ? "primary"
                            : "neutral"
                        }
                      >
                        {index + 1}
                      </StepIndicator>
                    }
                  >
                    <Typography level="title-md">
                      {formatAndCapitalizeString(progress.state)}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography level="body-sm">{progress.reason}</Typography>
                      <ButtonGroup variant="plain" spacing={1}>
                        {progress.start_date && (
                          <Chip size="sm">
                            {unixToDayjs(progress.start_date)?.format("DD MMM YYYY")}
                          </Chip>
                        )}
                        {progress.end_date && (
                          <Chip size="sm">
                            {unixToDayjs(progress.end_date)?.format("DD MMM YYYY")}
                          </Chip>
                        )}
                      </ButtonGroup>
                    </Stack>
                  </Step>
                )
              )}
            </Stepper>
          </Stack>
        ) : (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography level="title-md">Select one patient to view its history here</Typography>
          </Box>
        )}
        {commonNextEvents.length > 0 && (
          <Box
            sx={{
              p: 2,
              background: (theme) => theme.palette.background.surface,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
              {commonNextEvents.map((event) => (
                <Button
                  sx={{ whiteSpace: "nowrap" }}
                  key={event.event}
                  size="md"
                  onClick={() => {
                    setSelectedNextEvent(event);
                  }}
                  color="primary"
                >
                  {formatAndCapitalizeString(event.event)}
                </Button>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </DrawerWrapper>
  );
}
